import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
// Components
import HeadingBox from "../../../components/ui/HeadingBox";
import AppButton from "../../../components/ui/AppButton";
import AppCard from "../../../components/ui/AppCard";
// actions
import { useActions } from "../../../common/hooks/useActions";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
// helpers
import { getButtonText } from "./helpers/getButtonText";
import { getHeadingIcon } from "./helpers/getHeadingsIcon";
import { getContactInfoLink } from "./helpers/getContactInfoLink";
import { getFormattedDateWithHoursMinutes } from "./helpers/getFormattedDateWithHoursMinutes";
import { shouldShowLegacyErrorHeading } from "./helpers/shouldShowLegacyErrorHeading";
import { isProcessingAuthorizedBooking } from "./helpers/isProcessingAuthorizedBooking";
import { shouldStopBookingStatusPolling } from "./helpers/shouldStopBookingStatusPolling";
import { shouldShowMilesAndMore } from "../../../common/utils/transport/airlineBookingPayload";
// icons
import { errorIcon, noImgPlaceholder } from "../../../common/vector";

interface BookingConfirmationInterface {
  hidePdf?: boolean;
}

const BOOKING_STATUS_POLLING_TIMEOUT_MS = 2 * 60 * 1000;

export default function BookingConfirmation({ hidePdf }: BookingConfirmationInterface) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const language = localStorage.getItem("i18nextLng");
  const pollingStartedAtRef = useRef<number | null>(null);

  // states
  const apiUrl = useSelector(selectApiBaseUrl);
  const platformType = useSelector((state: any) => state.global.platformType);
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const bookingStatus = useSelector((state: any) => state.confirmation.bookingStatus);
  const apiError = useSelector((state: any) => state.confirmation.apiError);
  const isBookingStatusLoading = useSelector((state: any) => state.confirmation.isBookingStatusLoading);
  const isLoadingDownloadPdf = useSelector((state: any) => state.confirmation.isLoadingDownloadPdf);

  // actions
  const getBookingStatus = useActions(confirmationActionCreators?.getBookingStatusAction, []);
  const bookingStatusPollingTimeout = useActions(confirmationActionCreators?.bookingStatusPollingTimeoutAction, []);
  const downloadConfirmationPdf = useActions(confirmationActionCreators?.downloadConfirmationPdfAction, []);

  const isPaymentStatusAuthorized = bookingStatus?.payment_status === "authorized";
  const isUberBookingService = bookingStatus?.offer?.booking_and_availability_service === "UBER";
  const isProcessingYellowBookings = !bookingStatus?.directly_bookable && isPaymentStatusAuthorized;
  const statusMessage = bookingStatus?.booking_status_message;
  const showLegacyErrorHeading = shouldShowLegacyErrorHeading({
    statusMessage,
    apiError,
    bookingStatus: bookingStatus?.booking_status
  });

  const isProcessingAuthorized = isProcessingAuthorizedBooking(bookingStatus);

  const isAirline = platformType === "airline";
  const showMilesAndMore = shouldShowMilesAndMore(bookingStatus?.miles_and_more, isAirline);

  useEffect(() => {
    // TODO: try to handle turning off global loader on the global level (AppLayout ?), instead of on the component
    // not show global loader on this component
    dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
    dispatch(globalActionCreators?.toggleLanguageMenuDisableAction(true));

    return () => {
      dispatch(globalActionCreators?.toggleLanguageMenuDisableAction(false));
    };
  }, []);

  useEffect(() => {
    if (pollingStartedAtRef.current === null) {
      pollingStartedAtRef.current = Date.now();
    }

    // call getBookingStatus every 2 seconds until we get booking_status === "booked"
    const intervalId = setInterval(() => {
      // Stop polling after 2 min and treat as booking_status === "error"
      if (Date.now() - pollingStartedAtRef.current! > BOOKING_STATUS_POLLING_TIMEOUT_MS) {
        clearInterval(intervalId);
        bookingStatusPollingTimeout();
        return;
      }

      // don't call getBookingStatus if status is booked or cancelled or there is error from BE API
      // or in case Yellow bookings (when directly_bookable is set to false) and for PAYMENT_STATUS we get AUTHORIZED
      // or if we get payment_status=paid (that means user booked offer with price 0)
      if (shouldStopBookingStatusPolling(bookingStatus, apiError)) {
        clearInterval(intervalId);
        return;
      } else {
        apiUrl && getBookingStatus();
      }
    }, 2000);

    // clear interval if we get "booked" status
    if (bookingStatus?.booking_status === "booked") {
      clearInterval(intervalId);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [apiUrl, bookingStatus, apiError]);

  const handleButtonClick = () => {
    navigate(`/${language}`, { replace: true });
  };

  const srcSet =
    bookingStatus?.image_preview?.dimensions?.length > 0
      ? bookingStatus?.image_preview?.dimensions
          ?.map((image: any, index: number) => `${image.url} ${image.width}w`)
          .join(", ")
      : "";

  return (
    <Box
      className="confirmation"
      sx={{ py: 8, px: { xs: 4, sm: 4, md: 12, lg: 22 }, display: "flex", flexDirection: "column", flex: 1 }}
    >
      {isBookingStatusLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box mb={8}>{t("confirmation.preparing")}...</Box>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {showLegacyErrorHeading ? (
            // Legacy FE error copy with contact links — only when booking_status_message is missing
            <HeadingBox
              icon={errorIcon.default}
              title={t("confirmation.bookingError")}
              text={
                <span>
                  {t("confirmation.bookingErrorMessageInfo.infoText")}{" "}
                  <a href={getContactInfoLink(tenantInfo?.links)} target="_blank" rel="noreferrer">
                    {t("confirmation.bookingErrorMessageInfo.viaFormText")}
                  </a>{" "}
                  {t("confirmation.bookingErrorMessageInfo.viaEmailText")}{" "}
                  <a href={`mailto:support@get-local.com?subject=${bookingStatus?.booking_code}`}>
                    support@get-local.com
                  </a>
                </span>
              }
            />
          ) : (
            <HeadingBox
              icon={getHeadingIcon(bookingStatus?.booking_status)}
              title={statusMessage?.title ?? ""}
              htmlText={statusMessage?.text ?? ""}
              separator={true}
            />
          )}

          {(isProcessingAuthorized && !apiError) ||
          (isProcessingYellowBookings && !apiError) ||
          (bookingStatus?.booking_status === "booked" && !apiError) ||
          bookingStatus?.payment_status === "paid" ? (
            <Box sx={{ mt: 6 }}>
              <AppCard
                imgSrc={
                  (bookingStatus?.image_preview && bookingStatus?.image_preview?.dimensions?.[0]?.url) ||
                  noImgPlaceholder
                }
                imgSizes="(max-width: 600px) 40vw,
                (max-width: 900px) 361px,
                (max-width: 1200px) 494px,
                494px"
                imgSrcSet={srcSet}
                leftSection={
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box>
                      <span className="confirmation__booking-code">{bookingStatus?.booking_code}</span>
                      <span
                        className={`confirmation__booking-status ${
                          bookingStatus?.booking_status === "processing"
                            ? "confirmation__booking-status--yellow"
                            : bookingStatus?.booking_status === "error" || bookingStatus?.booking_status === "cancelled"
                            ? "confirmation__booking-status--error"
                            : ""
                        }`}
                      >
                        {bookingStatus?.booking_status === "processing"
                          ? t("confirmation.bookingRequestedLabel")
                          : bookingStatus?.booking_status_label}
                      </span>
                      <div className="confirmation__offer-name">{bookingStatus?.event_name}</div>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        justifyContent: "space-around",
                        fontFamily: "Neutraface2Light",
                        fontFeatureSettings: '"pnum" on, "lnum" on'
                      }}
                    >
                      <div>
                        {t("confirmation.bookingDate")}: {getFormattedDateWithHoursMinutes(bookingStatus?.booking_date)}
                      </div>
                      <div>
                        {t("confirmation.eventDate")}:{" "}
                        {getFormattedDateWithHoursMinutes(bookingStatus?.event_date, bookingStatus?.offer?.event_type)}
                      </div>
                      <div>
                        {bookingStatus?.units?.length > 0 &&
                          bookingStatus?.units?.map((unit: any) => unit?.label).join(", ")}
                      </div>
                      <div>{bookingStatus?.uber_price && `Estimated Fare: ${bookingStatus?.uber_price}`}</div>
                      <div>{bookingStatus?.service_fee && `Service fee: ${bookingStatus?.service_fee}`}</div>
                      <div>
                        {isUberBookingService ? t("confirmation.authorizedAmount") : t("confirmation.totalPrice")}:{" "}
                        {bookingStatus?.total_price}
                      </div>
                      {showMilesAndMore && (
                        <div>{t("confirmation.youCollected", { miles: bookingStatus?.miles_and_more })}</div>
                      )}
                      {platformType === "hotel" ? (
                        <div>
                          {t("confirmation.hotel")}: {bookingStatus?.hotel_name}, {bookingStatus?.hotel_city}
                        </div>
                      ) : null}
                      {bookingStatus?.details?.europcar_reservation_number && (
                        <div>
                          {t("confirmation.europcarReservationNumber")}:{" "}
                          {bookingStatus?.details?.europcar_reservation_number}
                        </div>
                      )}
                    </Box>
                  </Box>
                }
                rightSection={
                  bookingStatus?.pdf && bookingStatus?.booking_status !== "processing" && !hidePdf ? (
                    <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                      <Box sx={{ width: "100%" }}>
                        <AppButton
                          label={t("confirmation.printPdf")}
                          onClick={() =>
                            downloadConfirmationPdf(
                              bookingStatus?.pdf,
                              bookingStatus?.hash,
                              bookingStatus?.booking_code
                            )
                          }
                          variant="contained"
                          disableRipple={true}
                          customBackgroundColor={theme.palette.primary.light}
                          fontSize="14px"
                          isLoading={isLoadingDownloadPdf}
                          dataAttribute="print-pdf-button"
                        />
                      </Box>
                    </Box>
                  ) : (
                    <div></div>
                  )
                }
              />
            </Box>
          ) : null}

          <Box sx={{ mt: 6, maxWidth: "350px" }} margin="0 auto">
            <AppButton
              label={getButtonText(apiError, bookingStatus?.booking_status, t)}
              onClick={handleButtonClick}
              variant="contained"
              disableRipple={true}
              customBackgroundColor={tenantInfo?.style?.button?.color}
              customActiveColor={tenantInfo?.style?.button?.active}
              customHoverColor={tenantInfo?.style?.button?.hover}
              customClickColor={tenantInfo?.style?.button?.click}
              fontSize="14px"
              dataAttribute="confirmation-button"
            />
          </Box>
        </>
      )}
    </Box>
  );
}
